// AGC: UserManagement - Admin panel user management component
// AGC: Allows admins to view, create, and manage all users in the system

import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../hooks'
import {
  getAllUsers,
  updateUserRole,
  deactivateUser,
  reactivateUser,
} from '../../services/firebase-auth'
import type { UserProfile, UserRole } from '../../types/user'
import { getRoleDisplayName, getRoleBadgeColor } from '../../types/user'
import { ROLE_INFO, type FarmRole } from '../../types/rbac'

/**
 * AGC: UserManagement - Admin component for managing all users
 */
export const UserManagement: React.FC = () => {
  const { user: currentUser, getAuthToken } = useAuth()
  
  // AGC: State
  const [users, setUsers] = useState<UserProfile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  
  // AGC: Modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null)
  
  // AGC: Create user form
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    displayName: '',
    phoneNumber: '',
    role: 'user' as UserRole,
  })
  const [isCreating, setIsCreating] = useState(false)

  // AGC: Load users on mount
  useEffect(() => {
    loadUsers()
  }, [])

  // AGC: Load all users
  const loadUsers = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const usersList = await getAllUsers()
      setUsers(usersList)
    } catch (err: any) {
      console.error('❌ AGC: Error loading users:', err)
      setError(err.message || 'Erro ao carregar usuários')
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: Create new user via backend API
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsCreating(true)
    setError(null)

    try {
      const token = await getAuthToken()
      const response = await fetch('/api/v1/auth/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newUserData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao criar usuário')
      }

      // AGC: Refresh users list
      await loadUsers()
      
      // AGC: Reset form and close modal
      setNewUserData({
        email: '',
        password: '',
        displayName: '',
        phoneNumber: '',
        role: 'user',
      })
      setShowCreateModal(false)
    } catch (err: any) {
      console.error('❌ AGC: Error creating user:', err)
      setError(err.message || 'Erro ao criar usuário')
    } finally {
      setIsCreating(false)
    }
  }

  // AGC: Update user role
  const handleUpdateRole = async (uid: string, newRole: UserRole) => {
    try {
      await updateUserRole(uid, newRole)
      
      // AGC: Update local state
      setUsers(prev => prev.map(u => 
        u.uid === uid ? { ...u, role: newRole } : u
      ))
    } catch (err: any) {
      console.error('❌ AGC: Error updating role:', err)
      setError(err.message || 'Erro ao atualizar papel')
    }
  }

  // AGC: Toggle user active status
  const handleToggleStatus = async (uid: string, isActive: boolean) => {
    try {
      if (isActive) {
        await deactivateUser(uid)
      } else {
        await reactivateUser(uid)
      }
      
      // AGC: Update local state
      setUsers(prev => prev.map(u => 
        u.uid === uid ? { ...u, isActive: !isActive } : u
      ))
    } catch (err: any) {
      console.error('❌ AGC: Error toggling status:', err)
      setError(err.message || 'Erro ao alterar status')
    }
  }

  // AGC: Filter users
  const filteredUsers = users.filter(user => {
    // AGC: Search filter
    const matchesSearch = searchQuery === '' || 
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
    
    // AGC: Role filter
    const matchesRole = roleFilter === 'all' || user.role === roleFilter
    
    // AGC: Status filter
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive)
    
    return matchesSearch && matchesRole && matchesStatus
  })

  // AGC: Stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.isActive).length,
    admins: users.filter(u => u.role === 'admin').length,
    managers: users.filter(u => u.role === 'manager').length,
    submanagers: users.filter(u => u.role === 'submanager').length,
    users: users.filter(u => u.role === 'user').length,
  }

  // AGC: Build a map of ownerUid -> number of subaccounts created by that owner.
  // AGC: Used to show "X subcontas" for owners in the Conta column.
  const subaccountCountByOwner = useMemo(() => {
    const map = new Map<string, number>()
    for (const u of users) {
      const parent = u.createdBy || u.parentManagerId
      if (!parent) continue
      map.set(parent, (map.get(parent) || 0) + 1)
    }
    return map
  }, [users])

  // AGC: Quick lookup uid -> email for resolving parent account display
  const emailByUid = useMemo(() => {
    const map = new Map<string, string>()
    for (const u of users) {
      if (u.email) map.set(u.uid, u.email)
    }
    return map
  }, [users])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Carregando usuários...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* AGC: Error message */}
      {error && (
        <div className="bg-error-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 text-error-700 dark:text-red-400 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-error-700 dark:text-red-400 hover:opacity-70">
            ✕
          </button>
        </div>
      )}

      {/* AGC: Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Total" value={stats.total} icon="👥" color="bg-blue-500" />
        <StatCard label="Ativos" value={stats.active} icon="✅" color="bg-green-500" />
        <StatCard label="Admins" value={stats.admins} icon="🔐" color="bg-red-500" />
        <StatCard label="Gerentes" value={stats.managers} icon="👔" color="bg-purple-500" />
        <StatCard label="Sub-gerentes" value={stats.submanagers} icon="👤" color="bg-indigo-500" />
        <StatCard label="Usuários" value={stats.users} icon="🧑" color="bg-gray-500" />
      </div>

      {/* AGC: Actions bar */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* AGC: Search */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="🔍 Buscar por email ou nome..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-admin-bg-dark dark:text-slate-100"
            />
          </div>

          {/* AGC: Filters */}
          <div className="flex gap-2 flex-wrap">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
              className="px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg text-sm focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
            >
              <option value="all">Todos os papéis</option>
              <option value="admin">Admin</option>
              <option value="manager">Gerente</option>
              <option value="submanager">Sub-gerente</option>
              <option value="user">Usuário</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-3 py-2 border border-slate-200 dark:border-white/10 rounded-lg text-sm focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
            >
              <option value="all">Todos status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] rounded-lg hover:bg-brand-dark dark:hover:opacity-90 transition-colors flex items-center gap-2"
            >
              <span>➕</span>
              Novo Usuário
            </button>

            <button
              onClick={loadUsers}
              className="px-4 py-2 bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              title="Recarregar"
            >
              🔄
            </button>
          </div>
        </div>
      </div>

      {/* AGC: Users table */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-white/[0.02]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                  Usuário
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                  Papel
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                  Papel na Fazenda
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                  Conta / Subcontas
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                  Último Login
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/5">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    Nenhum usuário encontrado
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => {
                  const parentUid = user.createdBy || user.parentManagerId
                  const isSubaccount = !!parentUid
                  const subaccountCount = subaccountCountByOwner.get(user.uid) || 0
                  const farmRoleInfo = user.farmRole ? ROLE_INFO[user.farmRole as FarmRole] : null
                  return (
                  <tr key={user.uid} className={!user.isActive ? 'bg-slate-50 dark:bg-white/[0.02]' : ''}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 dark:bg-white/10 rounded-full flex items-center justify-center">
                          {user.photoURL ? (
                            <img src={user.photoURL} alt="" className="w-10 h-10 rounded-full" />
                          ) : (
                            <span className="text-slate-500 dark:text-slate-400 text-lg">
                              {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 dark:text-slate-100">
                            {user.displayName || 'Sem nome'}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateRole(user.uid, e.target.value as UserRole)}
                        disabled={user.uid === currentUser?.uid}
                        className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(user.role)} border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <option value="admin">Administrador</option>
                        <option value="manager">Gerente</option>
                        <option value="submanager">Sub-gerente</option>
                        <option value="user">Usuário</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {farmRoleInfo ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white ${farmRoleInfo.color}`}
                          title={farmRoleInfo.description}
                        >
                          <span>{farmRoleInfo.icon}</span>
                          <span>{farmRoleInfo.label}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.isActive
                          ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                          : 'bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400'
                      }`}>
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {isSubaccount ? (
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                            Subconta de
                          </span>
                          <span className="truncate max-w-[180px] block text-slate-700 dark:text-slate-300">
                            {(parentUid && emailByUid.get(parentUid)) || parentUid}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                            Proprietário
                          </span>
                          <span className="text-slate-700 dark:text-slate-300">
                            {subaccountCount === 0
                              ? 'Sem subcontas'
                              : subaccountCount === 1
                                ? '1 subconta'
                                : `${subaccountCount} subcontas`}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                      {user.lastLoginAt 
                        ? new Date(user.lastLoginAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'Nunca'
                      }
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleStatus(user.uid, user.isActive)}
                          disabled={user.uid === currentUser?.uid}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                            user.isActive
                              ? 'bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/25'
                              : 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400 hover:bg-green-100 dark:hover:bg-emerald-500/25'
                          }`}
                          title={user.isActive ? 'Desativar' : 'Reativar'}
                        >
                          {user.isActive ? '🚫' : '✅'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* AGC: Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-admin-card-dark dark:border dark:border-white/5 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                ➕ Criar Novo Usuário
              </h2>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={newUserData.displayName}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Senha *
                  </label>
                  <input
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={newUserData.phoneNumber}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder="+5541999999999"
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Papel *
                  </label>
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, role: e.target.value as UserRole }))}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                  >
                    <option value="user">Usuário</option>
                    <option value="submanager">Sub-gerente</option>
                    <option value="manager">Gerente</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="flex-1 px-4 py-2 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] rounded-lg hover:bg-brand-dark dark:hover:opacity-90 transition-colors disabled:opacity-50"
                  >
                    {isCreating ? 'Criando...' : 'Criar Usuário'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * AGC: Stat card component
 */
const StatCard: React.FC<{
  label: string
  value: number
  icon: string
  color: string
}> = ({ label, value, icon, color }) => (
  <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center text-white`}>
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">{value}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      </div>
    </div>
  </div>
)

export default UserManagement




